// Camera helper — wrapper kolem @capacitor/camera s web fallback (input file).

import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

export interface CapturedPhoto {
  blob: Blob;
  filename: string;
}

// ---------------------------------------------------------------------------
// Persistent camera stream — vyžádáme povolení jednou, stream držíme naživo
// aby prohlížeč (Safari) neptal na povolení při každém skenování.
// ---------------------------------------------------------------------------
let _stream: MediaStream | null = null;

/** Vrátí existující stream nebo vyžádá nový (jedno povolení za session). */
export async function getCameraStream(): Promise<MediaStream> {
  if (_stream && _stream.active) return _stream;
  // Na nativním Androidu musíme nejdřív explicitně požádat o permission přes
  // Capacitor Camera plugin — getUserMedia samo o sobě permission dialog nezobrazí.
  try {
    await Camera.requestPermissions({ permissions: ["camera"] });
  } catch { /* web / starší Capacitor — ignoruj, getUserMedia se postará */ }
  _stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
  return _stream;
}

/** Zavolej při ukončení směny — uvolní kameru. */
export function releaseCameraStream(): void {
  if (_stream) {
    _stream.getTracks().forEach((t) => t.stop());
    _stream = null;
  }
}

/** Open native camera (Capacitor) nebo file picker (web/PWA). */
export async function capturePhoto(): Promise<CapturedPhoto> {
  // Nativní camera activity potřebuje exkluzivní přístup — uvolníme getUserMedia
  // stream předem, aby po návratu BarcodeScanner dostal čerstvý stream místo zombie tracků.
  releaseCameraStream();
  const photo = await Camera.getPhoto({
    quality: 70,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    saveToGallery: false,
  });

  if (!photo.webPath) {
    throw new Error("Camera nevratila webPath");
  }

  // Fetch the local URI -> Blob
  const res = await fetch(photo.webPath);
  const blob = await res.blob();

  const ext = photo.format || "jpg";
  return {
    blob,
    filename: `photo.${ext}`,
  };
}
